import { useEffect, useMemo, useState, useRef } from 'react';

import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { EditorProps } from '@atlaskit/editor-core';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { type ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';

import type { createPageEditorPluginAIProvider } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import type { LoomProviderConfig } from '@confluence/fabric-providers';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import {
	type MentionType,
	useMentionsNotifications,
	handleMentionsChanged,
} from '@confluence/mentions-notifications';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api';
import { useSessionData } from '@confluence/session-data';
import { getIsHeadingLinksEnabled } from '@confluence/editor-heading-links';
import type { WithFlagsProps } from '@confluence/flags';
import type { EditorAPI } from '@confluence/loom-editor-utils';
import { useInitializeLoomPlugin, useLoomPluginOptions } from '@confluence/loom-editor-utils';
import {
	useEditorEngagementPlatform,
	useEditorPluginAIDefinitionsConfig,
	useEditorPluginAIDefinitionsEnabled,
	useEditorPluginAIProactiveConfig,
	useEditorPluginAIProactiveEnabled,
} from '@confluence/editor-features';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { isConfluenceRedirectToLoomSDKExperimentEnabled } from '@confluence/growth-experiment-helpers';
import type { ReferentialityContext } from '@confluence/app-referentiality';
import type { AIGlobalOptIn, EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features';
import type { FullPageAppearances } from '@confluence/content-appearance';

import { EditorLoomButton as Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton } from './Experiment_RedirectToSdkAfterCrossflow/EditorLoomButton';
import { EditorLoomButton } from './EditorLoomButton';
import type { FullPageEditorPresetOptions } from './createFullPageEditorPreset';
import { createFullPageEditorPreset } from './createFullPageEditorPreset';

export interface FullPageEditorPresetProps extends WithFlagsProps {
	appearance: FullPageAppearances;
	featureFlags: EditorProps['featureFlags'];
	onSave: EditorProps['onSave'];
	linking: EditorProps['linking'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	media: EditorProps['media'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	annotationProviders: EditorProps['annotationProviders'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	// collabEditProvider: EditorProps['collabEditProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	collabEdit: EditorProps['collabEdit'];
	primaryToolbarComponents: EditorProps['primaryToolbarComponents'];
	waitForMediaUpload: EditorProps['waitForMediaUpload'];
	mention: EditorProps['mention'];
	allowFindReplace: EditorProps['allowFindReplace'];
	allowTables: EditorProps['allowTables'];
	allowExtension: EditorProps['allowExtension'];
	extensionHandlers: EditorProps['extensionHandlers'];
	referentialityContext: ReferentialityContext;
	editorPluginAIProvider: ReturnType<typeof createPageEditorPluginAIProvider>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	elementBrowser: EditorProps['elementBrowser'];
	quickInsert: EditorProps['quickInsert'];
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPINext) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	getViewPagePath: () => string | null;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
}

export type FullPageEditorPresetAPINext = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorPresetProps): any {
	const { showObjectSidebar, hideObjectSidebar } = useObjectSidebarActions();
	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();
	const { isVariantCohort: isConfluenceRedirectToLoomSDKExperiment } =
		isConfluenceRedirectToLoomSDKExperimentEnabled(); // evaluate experiment here but does NOT fire exposure event

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);
	const previousEditorProps = usePreviousState(props);

	useEffect(() => {
		if (mentionChanges.length && isLivePage && fg('confluence_updated_mentions_livepages')) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const isHeadingLinksEnabled = getIsHeadingLinksEnabled(true);
	const textHighlightingFloatingToolbarExperiment =
		props.aiGlobalOptIn.status === 'disabled' &&
		expVal<boolean>('editor_text_highlighting_floating_toolbar', 'isEnabled', false);

	const editorAPIRef = useRef<FullPageEditorPresetAPINext | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: isConfluenceRedirectToLoomSDKExperiment
				? Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton
				: EditorLoomButton,
			editorAPIRef,
		});

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: props.aiGlobalOptIn.status,
	});

	const editorPluginAIProactiveConfig = useEditorPluginAIProactiveConfig({
		aiGlobalOptIn: props.aiGlobalOptIn,
		editorPluginAIFeedbackHandlers: props.editorPluginAIFeedbackHandlers,
	});

	// Memoise the preset otherwise we will re-render the editor too often
	const { preset, editorApi } = usePreset(() => {
		const { editorPluginAIProvider, aiGlobalOptIn } = props;
		const presetOptions: FullPageEditorPresetOptions = {
			...props,
			editorPluginAIProvider,
			aiGlobalOptIn,
			loomPluginOptions,
			isLoomPluginEnabled,
			allowExtension: props.allowExtension,
			prevAppearance: previousEditorProps?.appearance,
			handleMentionsChanged: (changes: MentionType[]) => {
				if (changes.length) {
					setMentionChanges(changes);
				}
			},
			editorPluginAIDefinitionsConfig,
			editorPluginAIDefinitionsEnabled,
			editorPluginAIProactiveEnabled,
			editorPluginAIProactiveConfig,
			editorEngagementPlatformPluginConfig,
			textHighlightingFloatingToolbarExperiment,
			isDesktop,
			isHeadingLinksEnabled,
			initialPluginConfiguration: {
				viewModePlugin: {
					initialContentMode: props.viewMode
						? props.hasEditPermission
							? 'live-edit'
							: 'live-view-only'
						: 'edit',
				},
			},
			...(fg('platform_editor_ai_object_sidebar_injection')
				? {
						contextPanelOptions: {
							objectSideBar: {
								showPanel: showObjectSidebar,
								closePanel: hideObjectSidebar,
							},
						},
					}
				: {}),
		};
		return createFullPageEditorPreset(presetOptions);
	}, [
		props.aiGlobalOptIn.status,
		isLoomPluginEnabled,
		loomPluginOptions,
		isHeadingLinksEnabled,
		props.getViewPagePath,
		editorEngagementPlatformPluginConfig,
		// update preset based on props change, originally from packages/editor/editor-core/src/create-editor/preset-utils.ts
		// persistScrollGutter not used, allowUndoRedoButtons and sanitizePrivateContent always true
		props.appearance,
		props.placeholder,
	]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom as EditorAPI['loom'],
		providerConfig: loomProviderConfig,
	});

	return preset;
}
